import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { MobileMenu } from '@/components/ui/mobile-menu';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { storeAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { 
  Scissors, 
  Calendar, 
  ShoppingBag, 
  Users, 
  Star, 
  ArrowRight,
  Search,
  MapPin,
  Clock,
  CheckCircle
} from 'lucide-react';

export default function LandingPage() {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const { user } = useAuth();
  const { isSingleStore, brand } = useBrand();
  const navigate = useNavigate();
  const isAuthenticated = !!user;

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      const response = await storeAPI.list();
      setStores(response.data);
    } catch (error) {
      console.error('Errore caricamento negozi:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    store.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleUserRedirect = () => {
    if (user?.role === 'ADMIN_PLATFORM') navigate('/admin/platform');
    else if (user?.role === 'ADMIN_STORE') navigate('/admin/store');
    else if (user?.role === 'BARBER') navigate('/dashboard/barber');
    else navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            {brand?.logoUrl ? (
              <img src={brand.logoUrl} alt={brand.appName} className="w-8 h-8 rounded object-cover" />
            ) : (
              <Scissors className="w-8 h-8 text-primary" />
            )}
            <span className="text-xl font-bold font-serif">{brand?.appName || 'AppLook'}</span>
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {!isSingleStore && (
              <>
                <a href="#features" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Funzionalità
                </a>
                <a href="#stores" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  Negozi
                </a>
              </>
            )}
            <ThemeToggle />
            {isAuthenticated ? (
              <Button onClick={handleUserRedirect} data-testid="dashboard-btn">
                Dashboard
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button variant="ghost" asChild data-testid="login-btn">
                  <Link to="/login">Accedi</Link>
                </Button>
                <Button asChild data-testid="register-btn">
                  <Link to="/register">Registrati</Link>
                </Button>
              </div>
            )}
          </nav>

          {/* Mobile Menu */}
          <MobileMenu />
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 section-padding hero-bg">
        <div className="container-main">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Prenota il tuo <span className="text-gradient">stile</span> in un click
              </h1>
              <p className="text-lg text-muted-foreground mb-8 max-w-lg">
                La piattaforma completa per barbieri e centri estetici. 
                Prenotazioni, shop e gestione in un&apos;unica soluzione.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                {!isSingleStore ? (
                  <>
                    <Button size="lg" className="gap-2" asChild data-testid="discover-btn">
                      <a href="#stores">
                        Scopri i Negozi
                        <ArrowRight className="w-4 h-4" />
                      </a>
                    </Button>
                    <Button size="lg" variant="outline" asChild data-testid="demo-btn">
                      <Link to="/s/demo">Prova la Demo</Link>
                    </Button>
                  </>
                ) : (
                  <>
                    <Button size="lg" className="gap-2" asChild data-testid="book-now-btn">
                      <Link to="/prenota">
                        Prenota Ora
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </Button>
                    {isAuthenticated && (
                      <Button size="lg" variant="outline" onClick={handleUserRedirect}>
                        Vai alla Dashboard
                      </Button>
                    )}
                  </>
                )}
              </div>
            </div>
            
            <div className="relative animate-slide-up">
              <img
                src="https://images.unsplash.com/photo-1549663369-22ac6b052faf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiYXJiZXIlMjBzaG9wJTIwaW50ZXJpb3IlMjBkYXJrJTIwYWVzdGhldGljfGVufDB8fHx8MTc2ODI2MDEzN3ww&ixlib=rb-4.1.0&q=85"
                alt="Barbiere al lavoro"
                className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-6 -left-6 glass-card p-4 flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Star className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">4.9/5</p>
                  <p className="text-sm text-muted-foreground">Valutazione media</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="section-padding bg-secondary/30">
        <div className="container-main">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Tutto ciò di cui hai bisogno
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Una piattaforma completa per gestire il tuo business e offrire la migliore esperienza ai tuoi clienti.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Calendar,
                title: 'Prenotazioni Online',
                description: 'Sistema di prenotazione con slot da 5 minuti e blocco automatico.'
              },
              {
                icon: ShoppingBag,
                title: 'Shop Prodotti',
                description: 'Vendi i tuoi prodotti direttamente dalla piattaforma.'
              },
              {
                icon: Users,
                title: 'Gestione Staff',
                description: 'Turni, assenze e calendario per ogni operatore.'
              },
              {
                icon: CheckCircle,
                title: 'Multi-Negozio',
                description: 'Una piattaforma, infinite possibilità. Gestisci più sedi.'
              }
            ].map((feature, index) => (
              <Card key={index} className="card-hover border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Stores Section - Only in multi-store mode */}
      {!isSingleStore && (
      <section id="stores" className="section-padding">
        <div className="container-main">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Trova il tuo negozio
            </h2>
            <p className="text-muted-foreground mb-8">
              Cerca tra i nostri partner e prenota subito il tuo appuntamento.
            </p>
            
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Cerca per nome o città..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-stores"
              />
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : filteredStores.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {searchQuery ? 'Nessun negozio trovato per la tua ricerca.' : 'Nessun negozio disponibile al momento.'}
              </p>
              <Button className="mt-4" asChild>
                <Link to="/s/demo">Prova la Demo</Link>
              </Button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredStores.map((store) => (
                <Card 
                  key={store.id} 
                  className="card-hover overflow-hidden"
                  data-testid={`store-card-${store.slug}`}
                >
                  <div 
                    className="h-40 bg-gradient-to-br from-primary/20 to-primary/5"
                    style={{
                      backgroundColor: store.branding?.primary_color || undefined
                    }}
                  />
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-xl mb-2">{store.name}</h3>
                    {store.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                        {store.description}
                      </p>
                    )}
                    {store.address && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mb-2">
                        <MapPin className="w-4 h-4" />
                        {store.address}
                      </p>
                    )}
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <Clock className="w-4 h-4" />
                      <span>Prenota 24/7</span>
                    </div>
                    <div className="flex gap-2">
                      <Button className="flex-1" asChild data-testid={`book-${store.slug}`}>
                        <Link to={`/s/${store.slug}`}>Prenota</Link>
                      </Button>
                      {(store.modules?.includes('SHOP') || store.active_modules?.includes('SHOP')) && (
                        <Button variant="outline" asChild data-testid={`shop-${store.slug}`}>
                          <Link to={`/s/${store.slug}/shop`}>Shop</Link>
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
      )}

      {/* CTA Section - different content for single vs multi store */}
      <section className="section-padding bg-primary text-primary-foreground">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            {isSingleStore ? 'Prenota il tuo appuntamento' : 'Pronto a iniziare?'}
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-lg mx-auto">
            {isSingleStore 
              ? 'Scegli il servizio che preferisci e prenota subito il tuo prossimo appuntamento.'
              : 'Registra il tuo negozio su AppLook e inizia a gestire prenotazioni e vendite in modo semplice e professionale.'
            }
          </p>
          {isSingleStore ? (
            <Button size="lg" variant="secondary" asChild data-testid="book-cta-btn">
              <Link to="/prenota" className="gap-2">
                Prenota Ora
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          ) : (
            <Button size="lg" variant="secondary" asChild data-testid="register-store-btn">
              <Link to="/register" className="gap-2">
                Registra il tuo Negozio
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t">
        <div className="container-main">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              {brand?.logoUrl ? (
                <img src={brand.logoUrl} alt={brand.appName} className="w-6 h-6 rounded object-cover" />
              ) : (
                <Scissors className="w-6 h-6 text-primary" />
              )}
              <span className="font-bold">{brand?.appName || 'AppLook'}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © 2025 AppLook. Tutti i diritti riservati.
            </p>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
              <a href="#" className="hover:text-foreground transition-colors">Termini</a>
              <a href="#" className="hover:text-foreground transition-colors">Contatti</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
